import { format } from 'date-fns';
import { z } from 'zod';

/**
 * Calendar day without time zone (YYYY-MM-DD), e.g. a birthdate or a campaign start date.
 * Values of the same kind can be compared as strings.
 *
 * Also accepts a `Date`, converted to its local calendar day, so handlers can return rows read from
 * a `timestamp` column: the driver parses those as local wall-clock time. JSON requests can't contain
 * a `Date`, so requests only accept `YYYY-MM-DD`.
 */
export const ZodBrandedDateString = z
  .union([
    z.iso.date(),
    z
      .date()
      .transform((value) => format(value, 'yyyy-MM-dd'))
      .pipe(z.iso.date()),
  ])
  .meta({ override: { type: 'string', format: 'date' } })
  .brand<'DateString'>();

export type DateString = z.output<typeof ZodBrandedDateString>;

/**
 * Instant as a UTC ISO string (YYYY-MM-DDTHH:mm:ss.sssZ), e.g. a creation timestamp.
 * Accepts a `Date` so handlers can return values read from the DB, which serialize to the same format.
 * Values of the same kind can be compared as strings.
 */
export const ZodBrandedDateTimeString = z
  .union([z.iso.datetime(), z.date()])
  .transform((value) => new Date(value).toISOString())
  .meta({ override: { type: 'string', format: 'date-time' } })
  .brand<'DateTimeString'>();

export type DateTimeString = z.output<typeof ZodBrandedDateTimeString>;
